import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Send, Search, Users, Brain, Wrench, GraduationCap,
  MessageCircle, HelpCircle, AlertTriangle, Crown,
  FileText, Calendar, Settings, ArrowRight, Home, ArrowLeft,
  Plus, Lock, Eye, EyeOff, ToggleLeft, ToggleRight, Star, Shield, Target, Clock, CheckSquare,
  Upload, Image, Trash2, Archive, Heart, TrendingUp, Compass, Edit, Save
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import UniversalHeartToggle from './UniversalHeartToggle';
import playNewPlayerArrivalChime from '../utils/newPlayerChime';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagingPage = ({ player, onNavigateHome, onNavigateToGame, onNavigateToMultiplayer }) => {
  const [currentView, setCurrentView] = useState('hub'); // hub, active_salon, remedy_council
  const [messages, setMessages] = useState([]);
  const [salonMessages, setSalonMessages] = useState({}); // Isolated messages per salon
  const [currentMessage, setCurrentMessage] = useState('');
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [userSalons, setUserSalons] = useState([]);
  const [mairyEnabled, setMairyEnabled] = useState(true);
  const [showMairySummary, setShowMairySummary] = useState(false);
  const [conversationSummary, setConversationSummary] = useState('');
  const [activeTab, setActiveTab] = useState('messages'); // messages, todo, calendar, docs, peeps, voting
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [newTodoPriority, setNewTodoPriority] = useState('3');
  const [newTodoDueDate, setNewTodoDueDate] = useState('');
  const [newTodoAssignee, setNewTodoAssignee] = useState('');
  const [newTodoNotes, setNewTodoNotes] = useState('');
  const [projectSettings, setProjectSettings] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([
    {
      id: 'event_1',
      title: 'Team Sync',
      date: new Date().toISOString().split('T')[0],
      time: '14:00',
      duration: '1h',
      attendees: [player.name],
      type: 'meeting'
    }
  ]);
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    time: '',
    duration: '1h',
    attendees: '',
    type: 'meeting',
    notes: ''
  });
  const [votes, setVotes] = useState([
    {
      id: '1',
      title: 'Should we proceed with Product Feature A?',
      description: 'Evaluating whether to implement the advanced analytics dashboard as our next major feature.',
      type: 'proposal', // proposal, decision, poll
      status: 'active', // active, closed, draft
      mandatory: true,
      deadline: '2025-10-01',
      created_by: 'Team Lead',
      created_at: new Date().toISOString(),
      votes: {
        yes: 3,
        no: 1,
        abstain: 0,
        total_members: 6
      },
      user_votes: [] // Track who voted what
    },
    {
      id: '2', 
      title: 'Budget allocation for Q4 marketing',
      description: 'Proposal to allocate $50k for digital marketing campaign in Q4.',
      type: 'decision',
      status: 'active',
      mandatory: false,
      deadline: '2025-09-25',
      created_by: 'Marketing Lead',
      created_at: new Date().toISOString(),
      votes: {
        yes: 2,
        no: 2,
        abstain: 1,
        total_members: 6
      },
      user_votes: []
    }
  ]);
  const messagesEndRef = useRef(null);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.12) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load user salons from localStorage and integrate with projects
  useEffect(() => {
    const savedSalons = localStorage.getItem('user_salons');
    if (savedSalons) {
      try {
        const parsedSalons = JSON.parse(savedSalons);
        setUserSalons(parsedSalons);
        console.log('📁 LOADED USER SALONS:', parsedSalons.length, 'projects found');
      } catch (error) {
        console.log('Could not parse saved salons');
      }
    }
  }, []);

  const handleSpiralCouncilRequest = () => {
    console.log('🌀 REMEDY COUNCIL: Opening guide first...');
    
    // STEP 1: Show guide information first (restore original workflow)
    setCurrentView('remedy_council');
  };

  const generateCounselorRecommendations = () => {
    const availableCounselors = [
      { name: 'Elder Sarah', specialty: 'Family dynamics and trust building', trust_score: 180, minimal_connections: true },
      { name: 'Marcus Chen', specialty: 'Business conflict resolution', trust_score: 165, minimal_connections: true },
      { name: 'Luna Rivera', specialty: 'Community mediation', trust_score: 175, minimal_connections: false },
      { name: 'Dr. Amanda Foster', specialty: 'Psychological healing and growth', trust_score: 190, minimal_connections: true }
    ];

    return availableCounselors.slice(0, 3);
  };

  const sendMessage = async () => {
    if (!currentMessage.trim()) return;

    try {
      console.log('📤 Sending project salon message:', currentMessage);
      
      const userMessage = {
        id: 'msg_' + Date.now(),
        content: currentMessage,
        sender: player.name,
        timestamp: new Date(),
        avatar: player.avatar_url
      };
      
      setMessages(prev => [...prev, userMessage]);

      // Enhanced smart todo detection
      const todoPatterns = [
        /need to\s+(.+)/i,
        /should\s+(.+)/i,
        /must\s+(.+)/i,
        /have to\s+(.+)/i,
        /remember to\s+(.+)/i,
        /deadline\s+(.+)/i,
        /by\s+\w+\s+(.+)/i,
        /due\s+(.+)/i,
        /task:\s*(.+)/i,
        /todo:\s*(.+)/i,
        /action:\s*(.+)/i,
        /follow.*up\s+(.+)/i
      ];
      
      const messageText = currentMessage.toLowerCase();
      const isTodoLike = todoPatterns.some(pattern => pattern.test(messageText)) ||
                        ['need to', 'should', 'must', 'have to', 'deadline', 'due', 'task', 'action', 'follow up'].some(keyword => messageText.includes(keyword));
      
      if (isTodoLike) {
        setTimeout(() => {
          const todoOfferMessage = {
            id: 'todo_offer_' + Date.now(),
            type: 'todo_offer',
            content: `💡 **Smart Detection**: This looks like a task! Would you like me to add it to your Apple Reminders-style todo list?`,
            sender: 'M.ai.ry Assistant',
            timestamp: new Date(),
            original_message: currentMessage,
            convert_to_todo: true,
            avatar: {
              name: 'M.ai.ry',
              image: 'https://api.dicebear.com/7.x/bottts/svg?seed=assistant&backgroundColor=22c55e'
            }
          };
          setMessages(prev => [...prev, todoOfferMessage]);
        }, 1000);
      }

      setCurrentMessage('');
      console.log('✅ Project salon message sent successfully');
      
    } catch (error) {
      console.error('❌ Project salon messaging error:', error);
      
      const errorMessage = {
        id: 'error_' + Date.now(),
        type: 'system_error',
        content: `🔧 Message sending failed. Please try again.`,
        sender: 'System',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const convertMessageToTodo = (messageText) => {
    const newTodoItem = {
      id: Date.now().toString(),
      text: messageText,
      completed: false,
      priority: messageText.toLowerCase().includes('urgent') ? 1 : 3,
      due_date: null,
      assigned_to: player.id,
      created_from_message: true,
      created_at: new Date().toISOString()
    };
    
    // Add todo to state immediately for live update
    setTodos(prev => [newTodoItem, ...prev]);
    
    // Enhanced confirmation with live feedback
    const confirmMessage = {
      id: 'todo_created_' + Date.now(),
      type: 'system',
      content: `✅ **Live Todo Created!**\n\nTask: "${messageText.substring(0, 60)}${messageText.length > 60 ? '...' : ''}"\n\n📋 Added to your Apple Reminders-style todo list\n🎯 Priority: ${newTodoItem.priority === 1 ? 'High' : newTodoItem.priority === 2 ? 'Medium' : 'Normal'}\n👤 Assigned to: ${player.name}`,
      sender: 'M.ai.ry Assistant',
      timestamp: new Date(),
      avatar: {
        name: 'M.ai.ry',
        image: 'https://api.dicebear.com/7.x/bottts/svg?seed=assistant&backgroundColor=22c55e'
      }
    };
    
    // Add confirmation to messages for immediate feedback
    setMessages(prev => [...prev, confirmMessage]);
    
    // Force re-render of todo tab if active
    if (activeTab === 'todo') {
      console.log('🔄 LIVE UPDATE: Todo list refreshed with new task');
    }
  };

  const createAdvancedTodo = () => {
    if (!newTodo.trim()) return;
    
    const newTodoItem = {
      id: Date.now().toString(),
      text: newTodo,
      completed: false,
      priority: parseInt(newTodoPriority),
      due_date: newTodoDueDate || null,
      assigned_to: newTodoAssignee || player.id,
      notes: newTodoNotes || '',
      created_from_message: false,
      created_at: new Date().toISOString()
    };
    
    // Add todo to state immediately for live update
    setTodos(prev => [newTodoItem, ...prev]);
    
    // Enhanced confirmation with live feedback
    const confirmMessage = {
      id: 'todo_created_' + Date.now(),
      type: 'system',
      content: `✅ **Advanced Todo Created!**\n\nTask: "${newTodo.substring(0, 60)}${newTodo.length > 60 ? '...' : ''}"\n\n📋 Added to your project todo list\n🎯 Priority: P${newTodoItem.priority}\n👤 Assigned to: ${newTodoAssignee || player.name}\n📅 Due: ${newTodoDueDate ? new Date(newTodoDueDate).toLocaleDateString() : 'No deadline'}\n📝 Notes: ${newTodoNotes ? 'Added' : 'None'}`,
      sender: 'M.ai.ry Assistant',
      timestamp: new Date(),
      avatar: {
        name: 'M.ai.ry',
        image: 'https://api.dicebear.com/7.x/bottts/svg?seed=assistant&backgroundColor=22c55e'
      }
    };
    
    // Add confirmation to messages for immediate feedback
    setMessages(prev => [...prev, confirmMessage]);
    
    // Reset form
    setNewTodo('');
    setNewTodoPriority('3');
    setNewTodoDueDate('');
    setNewTodoAssignee('');
    setNewTodoNotes('');
    
    // Force re-render of todo tab if active
    if (activeTab === 'todo') {
      console.log('🔄 LIVE UPDATE: Todo list refreshed with new advanced task');
    }
  };

  const createCalendarEvent = () => {
    if (!newEvent.title.trim() || !newEvent.date || !newEvent.time) {
      alert('Please fill in title, date, and time');
      return;
    }
    
    const eventItem = {
      id: `event_${Date.now()}`,
      title: newEvent.title,
      date: newEvent.date,
      time: newEvent.time,
      duration: newEvent.duration,
      attendees: newEvent.attendees ? newEvent.attendees.split(',').map(a => a.trim()) : [player.name],
      type: newEvent.type,
      notes: newEvent.notes,
      created_by: player.name,
      created_at: new Date().toISOString()
    };
    
    setCalendarEvents(prev => [...prev, eventItem]);
    
    // Success message
    const eventMessage = {
      id: 'event_created_' + Date.now(),
      type: 'system',
      content: `📅 **Event Created!**\n\n"${eventItem.title}"\n📅 ${new Date(eventItem.date).toLocaleDateString()}\n⏰ ${eventItem.time}\n👥 Attendees: ${eventItem.attendees.join(', ')}`,
      sender: 'Calendar Assistant',
      timestamp: new Date(),
      avatar: {
        name: 'Calendar',
        image: 'https://api.dicebear.com/7.x/bottts/svg?seed=calendar&backgroundColor=3b82f6'
      }
    };
    
    setMessages(prev => [...prev, eventMessage]);
    
    // Reset form
    setNewEvent({
      title: '',
      date: '',
      time: '',
      duration: '1h',
      attendees: '',
      type: 'meeting',
      notes: ''
    });
  };

  const openSalon = (salon) => {
    console.log('🎯 OPENING SALON:', salon.salon_name);
    setSelectedSalon(salon);
    
    // Load salon-specific messages (isolated per salon)
    const salonKey = salon.salon_id;
    setMessages(salonMessages[salonKey] || []);
    
    setCurrentView('active_salon');
  };

  // Update salon messages when messages change
  useEffect(() => {
    if (selectedSalon && messages.length > 0) {
      const salonKey = selectedSalon.salon_id;
      setSalonMessages(prev => ({
        ...prev,
        [salonKey]: messages
      }));
    }
  }, [messages, selectedSalon]);

  // MAIN HUB VIEW
  if (currentView === 'hub') {
    return (
      <div 
        className="min-h-screen relative"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        }}
      >
        {/* Grant spiral background */}
        <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `scale(8) rotate(${spiralPhase}deg)`,
              filter: 'blur(6px) saturate(1.8) brightness(1.4)',
              mixBlendMode: 'overlay'
            }}
          >
            <GrantitudeSpiral size={400} />
          </div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70"></div>

        <div className="relative z-10 max-w-6xl mx-auto p-6 pb-16">
          
          {/* Unified Header */}
          <div className="flex items-center justify-between mb-8 pt-8">
            <Button
              onClick={onNavigateToGame}
              variant="outline"
              className="text-white border-white/40 hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Game
            </Button>
            
            <div className="text-center">
              <h1 
                className="text-4xl font-bold text-white"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 25px rgba(255, 255, 255, 0.4)'
                }}
              >
                All Project Salons
              </h1>
              <p 
                className="text-cyan-200 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Ultimate Day Planner • Consciousness Collaboration
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => alert('Create new project salon coming soon...')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
              
              <Button
                onClick={() => {
                  const guildName = prompt("🏛️ Quick Guild Creation\n\nWhat's your guild name? (e.g., 'Mycology Collective', 'Regenerative Innovators')");
                  if (guildName && guildName.trim()) {
                    const guildDesc = prompt("✨ Guild Vision\n\nDescribe your guild in one line (max 100 chars):");
                    if (guildDesc && guildDesc.trim()) {
                      // Simple guild creation
                      alert(`🎉 Guild "${guildName}" created!\n\n"${guildDesc}"\n\nYour consciousness collective is ready for members. Start inviting seekers who resonate with your vision!`);
                      // TODO: Integrate with backend guild creation
                    }
                  }
                }}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              >
                <Crown className="w-4 h-4 mr-2" />
                Quick Guild
              </Button>
            </div>
          </div>

          {/* SINGLE COLLECTION: My Projects and Guilds */}
          <div className="mb-12">
            <div className="bg-black/40 backdrop-blur-xl border border-cyan-400/30 rounded-3xl p-8 shadow-2xl">
              <h2 
                className="text-3xl font-bold text-white mb-6 flex items-center space-x-3"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <Target className="w-8 h-8 text-cyan-400" />
                <span>My Projects and Guilds</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                
                {/* DYNAMIC: User Created Projects from Gameplay */}
                {userSalons.map((salon, index) => (
                  <button
                    key={salon.salon_id || salon.id}
                    onClick={() => openSalon({
                      salon_id: salon.salon_id || salon.id,
                      salon_name: salon.salon_name || salon.name,
                      type: 'user_created',
                      conversation_context: salon.conversation_context || ['User created project'],
                      created_at: salon.created_at || new Date().toISOString(),
                      health_status: 'healthy',
                      progress: 50,
                      members: 1
                    })}
                    className="group text-left"
                  >
                    <Card className="bg-black/40 backdrop-blur-xl border border-green-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                      <CardContent className="p-4">
                        <div className="flex items-start space-x-3 mb-3">
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-green-500 to-cyan-500 flex items-center justify-center shrink-0">
                            <Target className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white text-sm font-bold mb-1">{salon.salon_name || salon.name}</h3>
                            <Badge className="bg-green-500/30 text-green-300 text-xs">
                              From Game ✨
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                ))}
                
                {/* 1. Buy Mom a Car Project */}
                <button
                  onClick={() => openSalon({
                    salon_id: 'family_car_project',
                    salon_name: 'Buy Mom a Car 🚗',
                    type: 'family',
                    thumbnail: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=200&h=200&fit=crop&crop=center',
                    conversation_context: ['Planning family car purchase', 'Research and budgeting phase'],
                    created_at: new Date().toISOString(),
                    health_status: 'attention_needed',
                    progress: 65,
                    members: 4,
                    overdue_todos: 2
                  })}
                  className="group text-left"
                >
                  <Card className="bg-black/40 backdrop-blur-xl border border-cyan-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-cyan-400/50 shrink-0">
                          <img 
                            src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=200&h=200&fit=crop&crop=center"
                            alt="Car"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white text-sm font-bold mb-1">Buy Mom a Car 🚗</h3>
                          <Badge className="bg-yellow-500/30 text-yellow-300 text-xs">
                            <Clock className="w-2 h-2 mr-1" />
                            Needs Attention
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </button>

                {/* 2. One Private Salon Project */}
                <button
                  onClick={() => openSalon({
                    salon_id: 'private_consciousness_circle',
                    salon_name: 'Consciousness Circle 🌀',
                    type: 'private',
                    conversation_context: ['Private consciousness exploration'],
                    created_at: new Date().toISOString(),
                    health_status: 'excellent',
                    progress: 85
                  })}
                  className="group text-left"
                >
                  <Card className="bg-black/40 backdrop-blur-xl border border-purple-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-500 to-cyan-500 flex items-center justify-center shrink-0">
                          <Crown className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white text-sm font-bold mb-1">Consciousness Circle 🌀</h3>
                          <Badge className="bg-purple-500/30 text-purple-300 text-xs">Private</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </button>

                {/* 3. One Remedy Council */}
                <button
                  onClick={handleSpiralCouncilRequest}
                  className="group text-left"
                >
                  <Card className="bg-black/40 backdrop-blur-xl border border-yellow-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center shrink-0">
                          <Crown className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white text-sm font-bold mb-1">Remedy Council ⚖️</h3>
                          <Badge className="bg-yellow-500/30 text-yellow-300 text-xs">Sacred</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </button>

                {/* 4. Mycology Guild */}
                <button
                  onClick={() => openSalon({
                    salon_id: 'mycology_guild',
                    salon_name: 'Mycology Guild 🍄',
                    type: 'guild',
                    conversation_context: ['Fungi wisdom sharing', 'Charlie Felsher leading'],
                    created_at: new Date().toISOString(),
                    health_status: 'healthy',
                    progress: 75,
                    members: 8
                  })}
                  className="group text-left"
                >
                  <Card className="bg-black/40 backdrop-blur-xl border border-green-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shrink-0">
                          <span className="text-white text-lg">🍄</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white text-sm font-bold mb-1">Mycology Guild 🍄</h3>
                          <Badge className="bg-green-500/30 text-green-300 text-xs">Active Guild</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </button>
              </div>
            </div>
          </div>

          {/* NEW: My Company Category */}
          <div className="mb-12">
            <div className="bg-black/40 backdrop-blur-xl border border-blue-400/30 rounded-3xl p-8 shadow-2xl">
              <h2 
                className="text-3xl font-bold text-white mb-6 flex items-center space-x-3"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <Star className="w-8 h-8 text-blue-400" />
                <span>My Company</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                
                {/* New Product Development Project */}
                <button
                  onClick={() => openSalon({
                    salon_id: 'new_product_development',
                    salon_name: 'New Product Development 🚀',
                    type: 'business',
                    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=200&h=200&fit=crop&crop=center',
                    conversation_context: ['Product ideation phase', 'Market research active'],
                    created_at: new Date().toISOString(),
                    health_status: 'healthy',
                    progress: 45,
                    members: 6,
                    pending_votes: 2,
                    voting_active: true
                  })}
                  className="group text-left"
                >
                  <Card className="bg-black/40 backdrop-blur-xl border border-blue-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-blue-400/50 shrink-0">
                          <img 
                            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=200&h=200&fit=crop&crop=center"
                            alt="Product Development"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white text-sm font-bold mb-1">New Product Development 🚀</h3>
                          <div className="space-y-1">
                            <Badge className="bg-blue-500/30 text-blue-300 text-xs">Business</Badge>
                            <Badge className="bg-purple-500/30 text-purple-300 text-xs">
                              <Target className="w-2 h-2 mr-1" />
                              2 Votes Pending
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </button>
              </div>
            </div>
          </div>

          {/* Category Cards - Individual Standalone */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Remedy Council Category Card */}
            <button
              onClick={handleSpiralCouncilRequest}
              className="group text-left"
            >
              <Card className="bg-black/40 backdrop-blur-xl border border-yellow-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-yellow-600 to-orange-600 flex items-center justify-center shadow-lg">
                    <Crown className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 
                    className="text-white text-xl font-bold mb-3"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Remedy Council
                  </h3>
                  
                  <p 
                    className="text-white/80 leading-relaxed mb-4"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Sacred space for conflict resolution and trust building
                  </p>

                  <div className="flex items-center justify-center mt-4">
                    <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            </button>

            {/* Helpdesk Category Card */}
            <button
              onClick={() => openSalon({
                salon_id: 'helpdesk_main',
                salon_name: 'Technical Helpdesk 🔧',
                type: 'helpdesk',
                conversation_context: ['Technical support channel', 'CTO Alex available', 'Architect team ready'],
                created_at: new Date().toISOString()
              })}
              className="group text-left"
            >
              <Card className="bg-black/40 backdrop-blur-xl border border-green-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-green-600 to-emerald-600 flex items-center justify-center shadow-lg">
                    <Wrench className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 
                    className="text-white text-xl font-bold mb-3"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Helpdesk
                  </h3>
                  
                  <p 
                    className="text-white/80 leading-relaxed mb-4"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Messaging • Technical Support • Architect Team
                  </p>

                  <div className="space-y-1">
                    <Badge className="bg-green-600 text-white text-xs">CTO Alex Online</Badge>
                    <Badge className="bg-cyan-600 text-white text-xs">Ideas & Problems</Badge>
                  </div>

                  <div className="flex items-center justify-center mt-4">
                    <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            </button>

            {/* Mastery Exchange Category Card */}
            <button
              onClick={() => openSalon({
                salon_id: 'mastery_exchange_main',
                salon_name: 'Mastery Exchange 🎓',
                type: 'mastery',
                conversation_context: ['Skill sharing network', 'Master connections'],
                created_at: new Date().toISOString()
              })}
              className="group text-left"
            >
              <Card className="bg-black/40 backdrop-blur-xl border border-purple-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
                    <GraduationCap className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 
                    className="text-white text-xl font-bold mb-3"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Mastery Exchange
                  </h3>
                  
                  <p 
                    className="text-white/80 leading-relaxed mb-4"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Connect with masters for guidance and skill sharing
                  </p>

                  <div className="space-y-1">
                    <Badge className="bg-green-600 text-white text-xs">3 Masters Available</Badge>
                  </div>

                  <div className="flex items-center justify-center mt-4">
                    <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            </button>

            {/* Virtual Assistant Category Card */}
            <button
              onClick={() => openSalon({
                salon_id: 'virtual_assistant_main',
                salon_name: 'Virtual Assistant Network 🤖',
                type: 'virtual_assistant',
                conversation_context: ['AI assistant M.ai.ry ready', 'Human assistants available'],
                created_at: new Date().toISOString()
              })}
              className="group text-left"
            >
              <Card className="bg-black/40 backdrop-blur-xl border border-blue-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-cyan-600 to-blue-600 flex items-center justify-center shadow-lg">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 
                    className="text-white text-xl font-bold mb-3"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Virtual Assistant
                  </h3>
                  
                  <p 
                    className="text-white/80 leading-relaxed mb-4"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    AI and human assistants for various needs
                  </p>

                  <div className="space-y-1">
                    <Badge className="bg-green-600 text-white text-xs">M.ai.ry Online</Badge>
                  </div>

                  <div className="flex items-center justify-center mt-4">
                    <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            </button>

            {/* Wisdom Research Category Card */}
            <button
              onClick={() => openSalon({
                salon_id: 'wisdom_research_main',
                salon_name: 'Wisdom Research 📚',
                type: 'research',
                conversation_context: ['Research collaboration space', 'Songs of Grandmothers study'],
                created_at: new Date().toISOString()
              })}
              className="group text-left"
            >
              <Card className="bg-black/40 backdrop-blur-xl border border-indigo-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 
                    className="text-white text-xl font-bold mb-3"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Wisdom Research
                  </h3>
                  
                  <p 
                    className="text-white/80 leading-relaxed mb-4"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Deep exploration • Join Research Studies
                  </p>

                  <div className="space-y-1">
                    <Badge className="bg-indigo-600 text-white text-xs">Research Active</Badge>
                  </div>

                  <div className="flex items-center justify-center mt-4">
                    <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            </button>

          </div>
        </div>
        
        <UniversalHeartToggle />
        <PersistentFooter />
      </div>
    );
  }

  // ACTIVE SALON VIEW - Full Project Salon Interface
  if (currentView === 'active_salon' && selectedSalon) {
    return (
      <div 
        className="min-h-screen relative"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/20 to-black/70"></div>

        <div className="relative z-10 max-w-6xl mx-auto p-6 pb-24">
          
          {/* Enhanced Project Header with Health Status */}
          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 mb-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  onClick={() => setCurrentView('hub')}
                  variant="outline"
                  size="sm"
                  className="text-white border-white/40"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  All Projects
                </Button>
                
                <div className="flex items-center space-x-3">
                  {selectedSalon.thumbnail && (
                    <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-cyan-400/50">
                      <img 
                        src={selectedSalon.thumbnail}
                        alt={selectedSalon.salon_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div>
                    <h2 
                      className="text-white text-2xl font-bold"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      {selectedSalon.salon_name}
                    </h2>
                    <div className="flex items-center space-x-3 mt-1">
                      {selectedSalon.health_status && (
                        <Badge className={`text-xs ${
                          selectedSalon.health_status === 'excellent' ? 'bg-green-500/30 text-green-300' :
                          selectedSalon.health_status === 'healthy' ? 'bg-blue-500/30 text-blue-300' :
                          selectedSalon.health_status === 'attention_needed' ? 'bg-yellow-500/30 text-yellow-300' :
                          'bg-gray-500/30 text-gray-300'
                        }`}>
                          <Star className="w-3 h-3 mr-1" />
                          {selectedSalon.health_status?.replace('_', ' ')}
                        </Badge>
                      )}
                      <span className="text-white/70 text-sm">{selectedSalon.type}</span>
                      {selectedSalon.members && (
                        <span className="text-cyan-300 text-sm">{selectedSalon.members} members</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                {mairyEnabled && (
                  <Button
                    onClick={() => alert('M.ai.ry project guidance active')}
                    className="bg-purple-600/30 border border-purple-400/50 text-purple-300 w-full sm:w-auto"
                    size="sm"
                  >
                    <Brain className="w-3 h-3 mr-1" />
                    M.ai.ry
                  </Button>
                )}
                
                <Button
                  onClick={() => setProjectSettings(!projectSettings)}
                  variant="outline"
                  size="sm"
                  className="text-cyan-300 border-cyan-400 w-full sm:w-auto"
                >
                  <Settings className="w-3 h-3" />
                  <span className="ml-1 sm:hidden">Settings</span>
                </Button>
              </div>
            </div>
            
            {/* Project Settings Panel */}
            {projectSettings && (
              <div className="mt-6 p-4 bg-black/40 rounded-xl border border-white/10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Button
                    onClick={() => alert('Upload thumbnail coming soon...')}
                    variant="outline"
                    size="sm"
                    className="text-blue-300 border-blue-400"
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Thumbnail
                  </Button>
                  
                  <Button
                    onClick={() => alert('Archive project feature coming soon...')}
                    variant="outline"
                    size="sm"
                    className="text-yellow-300 border-yellow-400"
                  >
                    <Archive className="w-3 h-3 mr-1" />
                    Archive
                  </Button>
                  
                  <Button
                    onClick={() => alert('Privacy settings coming soon...')}
                    variant="outline"
                    size="sm"
                    className="text-green-300 border-green-400"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Privacy
                  </Button>
                  
                  <Button
                    onClick={() => {
                      const confirmed = window.confirm(
                        `⚠️ Delete Project: "${selectedSalon.salon_name}"\n\nThis will permanently delete all:\n• Messages and conversations\n• Tasks and calendar events\n• Project files and settings\n• Team member access\n\nThis action cannot be undone.\n\nAre you absolutely sure you want to delete this project?`
                      );
                      
                      if (confirmed) {
                        alert(`🗑️ Project "${selectedSalon.salon_name}" has been deleted.\n\nAll data has been permanently removed from the system.`);
                        // Return to main projects view
                        setCurrentView('hub');
                        setSelectedSalon(null);
                        setProjectSettings(false);
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="text-red-300 border-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete Project
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Dynamic Tab Content */}
          <div className="bg-black/30 backdrop-blur-xl rounded-2xl border border-white/20 p-6 mb-6 min-h-80">
            
            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white text-lg font-bold">Project Messages</h3>
                  <Button
                    onClick={() => setMairyEnabled(!mairyEnabled)}
                    className={`${mairyEnabled ? 'bg-purple-600/50 text-purple-200' : 'bg-gray-600/50 text-gray-300'} border border-purple-400/50`}
                    size="sm"
                  >
                    <Brain className="w-3 h-3 mr-1" />
                    M.ai.ry {mairyEnabled ? 'ON' : 'OFF'}
                  </Button>
                </div>
                
                {/* Messages Display - Futuristic Glass Design */}
                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {messages.map((msg, index) => (
                    <div key={msg.id || index} className="group">
                      {/* Futuristic Glass Message Container */}
                      <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-3 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                        <div className="flex items-start space-x-3">
                          {/* Minimalist Avatar */}
                          <div className="w-6 h-6 bg-gradient-to-br from-cyan-400/30 to-purple-500/30 rounded border border-white/20 flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {msg.sender[0]}
                          </div>
                          
                          {/* Message Content - Focus on Text */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-white font-medium text-sm">{msg.sender}</span>
                              <span className="text-white/40 text-xs">{msg.timestamp.toLocaleTimeString()}</span>
                            </div>
                            <p className="text-white/90 text-sm break-words leading-relaxed">{msg.content}</p>
                            
                            {/* Enhanced Todo Conversion with Glass Design */}
                            {msg.convert_to_todo && (
                              <div className="mt-3 p-3 bg-white/5 backdrop-blur-md rounded-lg border border-green-400/30">
                                <div className="flex items-center space-x-2 mb-2">
                                  <CheckSquare className="w-4 h-4 text-green-400" />
                                  <span className="text-green-200 font-medium text-xs">Convert to Todo?</span>
                                </div>
                                <div className="flex space-x-2">
                                  <Button
                                    onClick={() => {
                                      convertMessageToTodo(msg.original_message);
                                      setMessages(prev => prev.filter(m => m.id !== msg.id));
                                    }}
                                    size="sm"
                                    className="bg-green-500/20 text-green-200 border border-green-400/30 hover:bg-green-500/30"
                                  >
                                    ✅ Add Todo
                                  </Button>
                                  <Button
                                    onClick={() => setMessages(prev => prev.filter(m => m.id !== msg.id))}
                                    variant="outline"
                                    size="sm"
                                    className="text-white/60 border-white/20 hover:bg-white/10"
                                  >
                                    Skip
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {messages.length === 0 && (
                    <div className="text-center py-8 text-white/60">
                      <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                        Start the conversation! Share updates, ideas, or ask questions.
                      </p>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Message Input */}
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Share updates, ask questions, or describe tasks..."
                    className="flex-1 p-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!currentMessage.trim()}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* To Do Tab - Apple Reminders Style */}
            {activeTab === 'todo' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white text-lg font-bold">Project Tasks</h3>
                  <span className="text-white/60 text-sm">
                    {todos.filter(t => !t.completed).length} of {todos.length} remaining
                  </span>
                </div>
                
                {/* Enhanced Todo Creation - Futuristic Glass Design */}
                <div className="bg-white/5 backdrop-blur-xl border border-cyan-400/20 rounded-xl p-4 mb-4">
                  <h4 className="text-cyan-300 font-bold mb-3">✨ Create New Task</h4>
                  
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={newTodo}
                      onChange={(e) => setNewTodo(e.target.value)}
                      placeholder="What needs to be accomplished?"
                      className="w-full p-3 bg-black/20 backdrop-blur-md border border-white/20 rounded-lg text-white placeholder-white/50"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newTodo.trim()) {
                          e.preventDefault();
                          createAdvancedTodo();
                        }
                      }}
                    />
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <select 
                        value={newTodoPriority}
                        onChange={(e) => setNewTodoPriority(e.target.value)}
                        className="p-2 bg-black/30 border border-white/20 rounded text-white text-sm"
                      >
                        <option value="1">🔴 P1 - Urgent</option>
                        <option value="2">🟡 P2 - Important</option>
                        <option value="3">🟢 P3 - Normal</option>
                        <option value="4">🔵 P4 - Low</option>
                      </select>
                      
                      <input
                        type="date"
                        value={newTodoDueDate}
                        onChange={(e) => setNewTodoDueDate(e.target.value)}
                        className="p-2 bg-black/30 border border-white/20 rounded text-white text-sm"
                        min={new Date().toISOString().split('T')[0]}
                      />
                      
                      <select
                        value={newTodoAssignee}
                        onChange={(e) => setNewTodoAssignee(e.target.value)}
                        className="p-2 bg-black/30 border border-white/20 rounded text-white text-sm"
                      >
                        <option value="">Assign to...</option>
                        <option value={player.name}>👤 {player.name}</option>
                        <option value="team">👥 Team</option>
                        <option value="external">🤝 External</option>
                      </select>
                      
                      <Button
                        onClick={createAdvancedTodo}
                        disabled={!newTodo.trim()}
                        className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Create
                      </Button>
                    </div>
                    
                    {/* Notes Section */}
                    <textarea
                      value={newTodoNotes}
                      onChange={(e) => setNewTodoNotes(e.target.value)}
                      placeholder="Additional notes or context..."
                      className="w-full p-3 bg-black/20 backdrop-blur-md border border-white/20 rounded-lg text-white placeholder-white/50 text-sm h-20 resize-none"
                    />
                  </div>
                </div>
                
                {/* Enhanced Todo List - Futuristic Glass Design */}
                <div className="space-y-2">
                  {todos
                    .sort((a, b) => {
                      if (a.completed !== b.completed) return a.completed ? 1 : -1;
                      return (a.priority || 4) - (b.priority || 4);
                    })
                    .map((todo) => (
                    <div key={todo.id} className="group">
                      <div className="bg-white/5 backdrop-blur-xl hover:bg-white/10 rounded-xl p-4 border border-white/10 hover:border-cyan-400/30 transition-all">
                        
                        {/* Todo Header */}
                        <div className="flex items-start space-x-3 mb-2">
                          <button
                            onClick={() => {
                              setTodos(prev => prev.map(t => 
                                t.id === todo.id ? {...t, completed: !t.completed} : t
                              ));
                            }}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              todo.completed 
                                ? 'bg-green-500 border-green-500' 
                                : 'border-cyan-400 hover:border-cyan-300'
                            }`}
                          >
                            {todo.completed && <CheckSquare className="w-4 h-4 text-white" />}
                          </button>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className={`text-white font-medium ${todo.completed ? 'line-through opacity-60' : ''}`}>
                                {todo.text}
                              </span>
                              
                              {/* Priority Badge */}
                              {todo.priority === 1 && <Badge className="bg-red-500/30 text-red-300 text-xs">🔴 P1</Badge>}
                              {todo.priority === 2 && <Badge className="bg-yellow-500/30 text-yellow-300 text-xs">🟡 P2</Badge>}
                              {todo.priority === 3 && <Badge className="bg-green-500/30 text-green-300 text-xs">🟢 P3</Badge>}
                              {todo.priority === 4 && <Badge className="bg-blue-500/30 text-blue-300 text-xs">🔵 P4</Badge>}
                              
                              {todo.created_from_message && (
                                <Badge className="bg-purple-500/30 text-purple-200 text-xs">Smart Created</Badge>
                              )}
                            </div>
                            
                            {/* Todo Details */}
                            <div className="flex flex-wrap items-center gap-2 text-xs text-white/60">
                              {todo.assigned_to && (
                                <span className="flex items-center space-x-1">
                                  <Users className="w-3 h-3" />
                                  <span>{todo.assigned_to}</span>
                                </span>
                              )}
                              
                              {todo.due_date && (
                                <span className="flex items-center space-x-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>{new Date(todo.due_date).toLocaleDateString()}</span>
                                </span>
                              )}
                              
                              {todo.notes && (
                                <span className="flex items-center space-x-1">
                                  <FileText className="w-3 h-3" />
                                  <span>Notes</span>
                                </span>
                              )}
                            </div>
                            
                            {/* Notes Expansion */}
                            {todo.notes && (
                              <div className="mt-2 p-2 bg-black/20 rounded text-xs text-white/80 border-l-2 border-cyan-400/30">
                                {todo.notes}
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex space-x-1">
                            <button
                              onClick={() => {
                                if (confirm('Delete this todo?')) {
                                  setTodos(prev => prev.filter(t => t.id !== todo.id));
                                }
                              }}
                              className="p-1 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {todos.length === 0 && (
                    <div className="text-center py-8 text-white/60">
                      <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                        No tasks yet. Add one above or convert from messages!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Live Calendar System - Futuristic Glass Design */}
            {activeTab === 'calendar' && (
              <div className="space-y-6">
                
                {/* Event Creation - Glass Design */}
                <div className="bg-white/5 backdrop-blur-xl border border-blue-400/20 rounded-xl p-4">
                  <h4 className="text-blue-300 font-bold mb-3">📅 Create New Event</h4>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={newEvent.title}
                        onChange={(e) => setNewEvent(prev => ({...prev, title: e.target.value}))}
                        placeholder="Event title..."
                        className="p-3 bg-black/20 backdrop-blur-md border border-white/20 rounded-lg text-white placeholder-white/50"
                      />
                      
                      <select
                        value={newEvent.type}
                        onChange={(e) => setNewEvent(prev => ({...prev, type: e.target.value}))}
                        className="p-3 bg-black/30 border border-white/20 rounded-lg text-white"
                      >
                        <option value="meeting">🤝 Meeting</option>
                        <option value="deadline">⏰ Deadline</option>
                        <option value="workshop">🎓 Workshop</option>
                        <option value="review">📋 Review</option>
                        <option value="celebration">🎉 Celebration</option>
                      </select>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input
                        type="date"
                        value={newEvent.date}
                        onChange={(e) => setNewEvent(prev => ({...prev, date: e.target.value}))}
                        className="p-3 bg-black/30 border border-white/20 rounded-lg text-white"
                        min={new Date().toISOString().split('T')[0]}
                      />
                      
                      <input
                        type="time"
                        value={newEvent.time}
                        onChange={(e) => setNewEvent(prev => ({...prev, time: e.target.value}))}
                        className="p-3 bg-black/30 border border-white/20 rounded-lg text-white"
                      />
                      
                      <select
                        value={newEvent.duration}
                        onChange={(e) => setNewEvent(prev => ({...prev, duration: e.target.value}))}
                        className="p-3 bg-black/30 border border-white/20 rounded-lg text-white"
                      >
                        <option value="30m">30 minutes</option>
                        <option value="1h">1 hour</option>
                        <option value="1.5h">1.5 hours</option>
                        <option value="2h">2 hours</option>
                        <option value="4h">4 hours</option>
                        <option value="8h">All day</option>
                      </select>
                    </div>
                    
                    <input
                      type="text"
                      value={newEvent.attendees}
                      onChange={(e) => setNewEvent(prev => ({...prev, attendees: e.target.value}))}
                      placeholder="Attendees (comma separated)..."
                      className="w-full p-3 bg-black/20 backdrop-blur-md border border-white/20 rounded-lg text-white placeholder-white/50"
                    />
                    
                    <div className="flex space-x-3">
                      <textarea
                        value={newEvent.notes}
                        onChange={(e) => setNewEvent(prev => ({...prev, notes: e.target.value}))}
                        placeholder="Event notes or agenda..."
                        className="flex-1 p-3 bg-black/20 backdrop-blur-md border border-white/20 rounded-lg text-white placeholder-white/50 text-sm h-20 resize-none"
                      />
                      
                      <Button
                        onClick={createCalendarEvent}
                        disabled={!newEvent.title.trim() || !newEvent.date || !newEvent.time}
                        className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Create Event
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Live Calendar Display */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl p-4">
                  <h4 className="text-white font-bold mb-4">📅 Upcoming Events</h4>
                  
                  <div className="space-y-3">
                    {calendarEvents
                      .sort((a, b) => new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time))
                      .map((event) => (
                      <div key={event.id} className="bg-black/20 backdrop-blur-md rounded-lg p-3 border border-white/10 hover:border-blue-400/30 transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              event.type === 'meeting' ? 'bg-blue-500' :
                              event.type === 'deadline' ? 'bg-red-500' :
                              event.type === 'workshop' ? 'bg-green-500' :
                              event.type === 'review' ? 'bg-yellow-500' :
                              'bg-purple-500'
                            }`} />
                            <span className="text-white font-medium">{event.title}</span>
                          </div>
                          
                          <button
                            onClick={() => {
                              if (confirm('Delete this event?')) {
                                setCalendarEvents(prev => prev.filter(e => e.id !== event.id));
                              }
                            }}
                            className="text-red-400 hover:text-red-300 text-xs opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 text-xs text-white/60">
                          <span className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(event.date).toLocaleDateString()}</span>
                          </span>
                          
                          <span className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{event.time} ({event.duration})</span>
                          </span>
                          
                          <span className="flex items-center space-x-1">
                            <Users className="w-3 h-3" />
                            <span>{event.attendees.join(', ')}</span>
                          </span>
                        </div>
                        
                        {event.notes && (
                          <div className="mt-2 p-2 bg-black/30 rounded text-xs text-white/80 border-l-2 border-blue-400/30">
                            {event.notes}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {calendarEvents.length === 0 && (
                      <div className="text-center py-8 text-white/60">
                        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p>No events scheduled. Create one above!</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Voting Tab - Loomio Style Consensus */}
            {activeTab === 'voting' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white text-lg font-bold">Project Voting</h3>
                  <Button
                    onClick={() => alert('Create new vote coming soon...')}
                    className="bg-purple-600/50 text-purple-200 border border-purple-400/50"
                    size="sm"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    New Vote
                  </Button>
                </div>
                
                {/* Active Votes */}
                <div className="space-y-4">
                  {votes.map((vote) => (
                    <div key={vote.id} className="bg-white/10 rounded-xl p-6 border border-white/20">
                      
                      {/* Vote Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-white text-lg font-bold mb-2">{vote.title}</h4>
                          <p className="text-white/80 text-sm mb-3">{vote.description}</p>
                          
                          <div className="flex items-center space-x-3">
                            <Badge className={`text-xs ${vote.mandatory ? 'bg-red-500/30 text-red-300' : 'bg-blue-500/30 text-blue-300'}`}>
                              {vote.mandatory ? 'Mandatory' : 'Optional'}
                            </Badge>
                            <Badge className="bg-gray-500/30 text-gray-300 text-xs">
                              {vote.type}
                            </Badge>
                            {vote.deadline && (
                              <span className="text-yellow-300 text-xs">
                                Due: {new Date(vote.deadline).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Vote Progress Bar - Loomio Style */}
                      <div className="mb-4">
                        <div className="flex justify-between text-sm text-white/70 mb-2">
                          <span>Consensus Progress</span>
                          <span>{vote.votes.yes + vote.votes.no + vote.votes.abstain}/{vote.votes.total_members} voted</span>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
                          <div className="flex h-full">
                            <div 
                              className="bg-green-500"
                              style={{ width: `${(vote.votes.yes / vote.votes.total_members) * 100}%` }}
                            ></div>
                            <div 
                              className="bg-red-500"
                              style={{ width: `${(vote.votes.no / vote.votes.total_members) * 100}%` }}
                            ></div>
                            <div 
                              className="bg-gray-500"
                              style={{ width: `${(vote.votes.abstain / vote.votes.total_members) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        {/* Vote Counts */}
                        <div className="flex justify-between text-xs mt-2">
                          <span className="text-green-300">{vote.votes.yes} Yes</span>
                          <span className="text-red-300">{vote.votes.no} No</span>
                          <span className="text-gray-300">{vote.votes.abstain} Abstain</span>
                        </div>
                      </div>
                      
                      {/* Voting Buttons */}
                      <div className="flex space-x-3">
                        <Button
                          onClick={() => {
                            setVotes(prev => prev.map(v => 
                              v.id === vote.id 
                                ? {...v, votes: {...v.votes, yes: v.votes.yes + 1}}
                                : v
                            ));
                          }}
                          className="flex-1 bg-green-500/30 hover:bg-green-500/50 text-green-200 border border-green-400/50"
                          size="sm"
                        >
                          <CheckSquare className="w-3 h-3 mr-1" />
                          Yes
                        </Button>
                        
                        <Button
                          onClick={() => {
                            setVotes(prev => prev.map(v => 
                              v.id === vote.id 
                                ? {...v, votes: {...v.votes, no: v.votes.no + 1}}
                                : v
                            ));
                          }}
                          className="flex-1 bg-red-500/30 hover:bg-red-500/50 text-red-200 border border-red-400/50"
                          size="sm"
                        >
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          No
                        </Button>
                        
                        <Button
                          onClick={() => {
                            setVotes(prev => prev.map(v => 
                              v.id === vote.id 
                                ? {...v, votes: {...v.votes, abstain: v.votes.abstain + 1}}
                                : v
                            ));
                          }}
                          variant="outline"
                          className="flex-1 text-gray-300 border-gray-400/50"
                          size="sm"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Abstain
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {votes.length === 0 && (
                    <div className="text-center py-8 text-white/60">
                      <Target className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                        No active votes. Create one to start team consensus building!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Other tabs - Docs and Peeps */}
            {activeTab === 'docs' && (
              <div className="text-center py-16">
                <FileText className="w-16 h-16 mx-auto mb-4 text-orange-400/60" />
                <h3 className="text-white text-xl font-bold mb-2">Documents & Links</h3>
                <p className="text-white/70">Coming soon...</p>
              </div>
            )}
            
            {activeTab === 'peeps' && (
              <div className="text-center py-16">
                <Users className="w-16 h-16 mx-auto mb-4 text-pink-400/60" />
                <h3 className="text-white text-xl font-bold mb-2">Team Members</h3>
                <p className="text-white/70">Coming soon...</p>
              </div>
            )}
          </div>
          
          {/* Future Tools & Workflows - Added to Every Project Salon */}
          <div className="mt-6 bg-gradient-to-r from-indigo-900/40 to-purple-900/40 backdrop-blur-xl rounded-2xl p-6 border border-indigo-400/30">
            <h4 
              className="text-indigo-200 text-lg font-bold mb-4 text-center"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              🛠️ Consciousness Tools & Workflows (Coming Soon)
            </h4>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Button
                onClick={() => alert('Business Model Generation - Analog simulation workflow for consciousness-driven ventures coming soon...')}
                variant="outline"
                size="sm"
                className="text-indigo-300 border-indigo-400/50 hover:bg-indigo-500/20"
              >
                <Target className="w-3 h-3 mr-1" />
                Business Model Gen
              </Button>
              
              <Button
                onClick={() => alert('Vision Creation Worksheet - Beyond Entrepreneurship 2.0 methodology for consciousness expansion coming soon...')}
                variant="outline"
                size="sm"
                className="text-purple-300 border-purple-400/50 hover:bg-purple-500/20"
              >
                <Star className="w-3 h-3 mr-1" />
                Vision Worksheet
              </Button>
              
              <Button
                onClick={() => alert('Brand-Name Builder - Consciousness-aligned brand creation tools coming soon...')}
                variant="outline"
                size="sm"
                className="text-cyan-300 border-cyan-400/50 hover:bg-cyan-500/20"
              >
                <Heart className="w-3 h-3 mr-1" />
                Brand Builder
              </Button>
              
              <Button
                onClick={() => alert('Eight Compass Point Phases - WisconSing methodology for project completion coming soon...')}
                variant="outline"
                size="sm"
                className="text-yellow-300 border-yellow-400/50 hover:bg-yellow-500/20"
              >
                <Compass className="w-3 h-3 mr-1" />
                8 Compass Points
              </Button>
              
              <Button
                onClick={() => alert('Retreat/Event Producer - Consciousness gathering coordination tools coming soon...')}
                variant="outline"
                size="sm"
                className="text-green-300 border-green-400/50 hover:bg-green-500/20"
              >
                <Users className="w-3 h-3 mr-1" />
                Event Producer
              </Button>
              
              <Button
                onClick={() => alert('Conscious Stock Contribution - Trust-based investment collaboration coming soon...')}
                variant="outline"
                size="sm"
                className="text-orange-300 border-orange-400/50 hover:bg-orange-500/20"
              >
                <TrendingUp className="w-3 h-3 mr-1" />
                Stock Contribution
              </Button>
            </div>
            
            <p 
              className="text-indigo-100 text-xs text-center mt-4 italic"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Revolutionary consciousness-driven business and collaboration tools
            </p>
          </div>
        </div>

        {/* Mobile App-Style Bottom Navigation - Enhanced with Voting */}
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-gray-900/95 backdrop-blur-xl border-t border-gray-700">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center justify-around">
              
              {/* Messages */}
              <button
                onClick={() => setActiveTab('messages')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeTab === 'messages' ? 'text-purple-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-xs font-medium">Messages</span>
              </button>
              
              {/* To Do */}
              <button
                onClick={() => setActiveTab('todo')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeTab === 'todo' ? 'text-green-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <CheckSquare className="w-5 h-5" />
                <span className="text-xs font-medium">To Do</span>
              </button>
              
              {/* Calendar */}
              <button
                onClick={() => setActiveTab('calendar')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeTab === 'calendar' ? 'text-blue-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <Calendar className="w-5 h-5" />
                <span className="text-xs font-medium">Calendar</span>
              </button>
              
              {/* Voting - NEW */}
              <button
                onClick={() => setActiveTab('voting')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeTab === 'voting' ? 'text-purple-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <Target className="w-5 h-5" />
                <span className="text-xs font-medium">Voting</span>
              </button>
              
              {/* Docs */}
              <button
                onClick={() => setActiveTab('docs')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeTab === 'docs' ? 'text-orange-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <FileText className="w-5 h-5" />
                <span className="text-xs font-medium">Docs</span>
              </button>
              
              {/* Peeps */}
              <button
                onClick={() => setActiveTab('peeps')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeTab === 'peeps' ? 'text-pink-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="text-xs font-medium">Peeps</span>
              </button>
            </div>
          </div>
        </div>
        
        <UniversalHeartToggle />
        <PersistentFooter />
      </div>
    );
  }

  // REMEDY COUNCIL VIEW
  if (currentView === 'remedy_council') {
    return (
      <div 
        className="min-h-screen relative"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/20 to-black/70"></div>

        <div className="relative z-10 max-w-4xl mx-auto p-6">
          
          {/* Remedy Council Header */}
          <div className="text-center pt-8 mb-8">
            <Button
              onClick={() => setCurrentView('hub')}
              variant="outline" 
              className="text-white border-white/40 hover:bg-white/10 mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Project Salons
            </Button>
            
            <div className="bg-black/60 backdrop-blur-xl rounded-3xl p-8 border border-yellow-400/30 shadow-2xl">
              <Crown className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
              
              <h1 
                className="text-4xl font-bold text-white mb-4"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 25px rgba(255, 255, 255, 0.4)'
                }}
              >
                Remedy Council
              </h1>
              
              <p 
                className="text-yellow-100 text-xl mb-6 leading-relaxed"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Sacred space for resolving conflict, building trust, and generating solutions that honor both individual and collective wisdom.
              </p>
              
              {/* Enhanced Process Description */}
              <div className="bg-yellow-900/30 rounded-2xl p-6 border border-yellow-400/30 mb-6">
                <h3 className="text-yellow-200 text-lg font-bold mb-4">Council Guidance & Selection</h3>
                <div className="space-y-4 text-yellow-100">
                  <div>
                    <h4 className="font-medium mb-2">🧭 Counselor Selection Criteria:</h4>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>Only those with bandwidth and presence should step in</li>
                      <li>Counselors should have minimal emotional connections to stakeholders</li>
                      <li>Neutral parties with high trust scores preferred</li>
                      <li>Experience in conflict resolution and mediation valued</li>
                      <li>Commitment to compassion before correction</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">⚖️ Sacred Principles:</h4>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>Compassion before correction</li>
                      <li>Belonging over winning</li>
                      <li>Dissonance is not failure—it's energy seeking resolution</li>
                      <li>Music and rhythm are tools of coherence</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">🌀 Process Modes:</h4>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>Preferably in-person (IRL) for maximum resonance</li>
                      <li>Asynchronous (written, one-on-one) acceptable</li>
                      <li>Virtual councils supported when needed</li>
                      <li>Begin with silence or breathwork to center</li>
                      <li>Consider toning, percussion, or musical intermissions</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-900/30 rounded-2xl p-6 border border-yellow-400/30">
                <h3 className="text-yellow-200 text-lg font-bold mb-3">Council Process</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">1</div>
                    <p className="text-yellow-200">Opening</p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">2</div>
                    <p className="text-yellow-200">Expression</p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">3</div>
                    <p className="text-yellow-200">Listening</p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">4</div>
                    <p className="text-yellow-200">Integration</p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">5</div>
                    <p className="text-yellow-200">Resolution</p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">6</div>
                    <p className="text-yellow-200">Closing</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Council Request Form */}
          <div className="bg-black/40 backdrop-blur-xl border border-yellow-400/30 rounded-2xl p-8 shadow-xl">
            <div className="space-y-6">
              <div>
                <label 
                  className="block text-white text-lg mb-3"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Describe the situation requiring council wisdom:
                </label>
                <textarea
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  placeholder="Share the complexity that would benefit from neutral counsel..."
                  className="w-full p-4 bg-white/10 border border-white/30 rounded-xl text-white resize-none focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                  style={{ 
                    fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                    minHeight: '150px'
                  }}
                  rows={6}
                />
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={() => {
                    // STEP 2: Now create actual council with user input
                    const councilWorksheet = {
                      situation_description: currentMessage || '',
                      stakeholders: [player.name],
                      recommended_counselors: generateCounselorRecommendations(),
                      process_stage: 'stakeholder_review',
                      created_at: new Date(),
                      salon_name: `Remedy Council - ${new Date().toLocaleDateString()}`,
                      project_type: 'remedy_council',
                      council_authority: true
                    };

                    localStorage.setItem('pending_council_request', JSON.stringify(councilWorksheet));
                    
                    // Create comprehensive first message with user situation + instructions
                    const comprehensiveFirstMessage = {
                      id: 'council_activated_' + Date.now(),
                      type: 'remedy_council_activation',
                      content: `🌀 **REMEDY COUNCIL ACTIVATED**

**SITUATION REQUIRING COUNCIL**: ${currentMessage || 'General guidance requested'}

**COUNCIL WORKSPACE NOTES & INSTRUCTIONS**:

**PURPOSE**: Sacred space for resolving conflict, building trust, and generating solutions that honor both individual and collective.

**COUNCIL PROCESS (6 Sacred Phases):**
1. **OPENING**: Grounding practice (3-breath embrace, harmonic tone). Optional: participants share positive memory about the other.
2. **EXPRESSION**: Talking stick method - one voice at a time. Everyone speaks freely before responses begin.
3. **LISTENING**: All responses must reflect empathy and understanding. Practice deep listening: hear for self, other, and whole.
4. **INTEGRATION**: Reflection circle restating what was heard. Optional music/drumming intermission.
5. **RESOLUTION**: Develop Memo of Understanding with agreements, actionable steps, accountability plan.
6. **CLOSING**: Optional shared meal or drumming circle. Affirm collective belonging and gratitude.

**KEY PRINCIPLES**:
• Compassion before correction
• Belonging over winning  
• Dissonance is not failure—it's energy seeking resolution
• Music and rhythm are tools of coherence

**RECOMMENDED COUNSELORS**: ${generateCounselorRecommendations()?.map(c => `${c?.name || 'Unknown'} (${c?.specialty || 'General'}, Trust: ${c?.trust_score || 'N/A'})`).join(', ') || 'None available'}

This council workspace is now ready. Begin when all stakeholders are prepared for sacred dialogue.`,
                      sender: 'Remedy Council System',
                      timestamp: new Date(),
                      council_data: councilWorksheet
                    };

                    // Set ISOLATED messages for this salon only
                    setSelectedSalon(councilWorksheet);
                    setMessages([comprehensiveFirstMessage]); // Isolated message set
                    setCurrentView('active_salon');
                  }}
                  className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-8 py-4 text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Crown className="w-6 h-6 mr-3" />
                  Activate Remedy Council
                </Button>
              </div>
            </div>
          </div>

        {/* Future Tools & Workflows - Placeholder Links */}
        <div className="mt-8 bg-gradient-to-r from-indigo-900/40 to-purple-900/40 backdrop-blur-xl rounded-2xl p-6 border border-indigo-400/30">
          <h4 
            className="text-indigo-200 text-lg font-bold mb-4 text-center"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            🛠️ Consciousness Tools & Workflows (Coming Soon)
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Button
              onClick={() => alert('Business Model Generation - Analog simulation workflow for consciousness-driven ventures coming soon...')}
              variant="outline"
              size="sm"
              className="text-indigo-300 border-indigo-400/50 hover:bg-indigo-500/20"
            >
              <Target className="w-3 h-3 mr-1" />
              Business Model Gen
            </Button>
            
            <Button
              onClick={() => alert('Vision Creation Worksheet - Beyond Entrepreneurship 2.0 methodology for consciousness expansion coming soon...')}
              variant="outline"
              size="sm"
              className="text-purple-300 border-purple-400/50 hover:bg-purple-500/20"
            >
              <Star className="w-3 h-3 mr-1" />
              Vision Worksheet
            </Button>
            
            <Button
              onClick={() => alert('Brand-Name Builder - Consciousness-aligned brand creation tools coming soon...')}
              variant="outline"
              size="sm"
              className="text-cyan-300 border-cyan-400/50 hover:bg-cyan-500/20"
            >
              <Heart className="w-3 h-3 mr-1" />
              Brand Builder
            </Button>
            
            <Button
              onClick={() => alert('Eight Compass Point Phases - WisconSing methodology for project completion coming soon...')}
              variant="outline"
              size="sm"
              className="text-yellow-300 border-yellow-400/50 hover:bg-yellow-500/20"
            >
              <Compass className="w-3 h-3 mr-1" />
              8 Compass Points
            </Button>
            
            <Button
              onClick={() => alert('Retreat/Event Producer - Consciousness gathering coordination tools coming soon...')}
              variant="outline"
              size="sm"
              className="text-green-300 border-green-400/50 hover:bg-green-500/20"
            >
              <Users className="w-3 h-3 mr-1" />
              Event Producer
            </Button>
            
            <Button
              onClick={() => alert('Conscious Stock Contribution - Trust-based investment collaboration coming soon...')}
              variant="outline"
              size="sm"
              className="text-orange-300 border-orange-400/50 hover:bg-orange-500/20"
            >
              <TrendingUp className="w-3 h-3 mr-1" />
              Stock Contribution
            </Button>
          </div>
          
          <p 
            className="text-indigo-100 text-xs text-center mt-4 italic"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            Revolutionary consciousness-driven business and collaboration tools
          </p>
        </div>
        
        {/* Future Tools & Workflows - Visible in Every Project Salon */}
        <div className="mt-6 mb-20 bg-gradient-to-r from-indigo-900/40 to-purple-900/40 backdrop-blur-xl rounded-2xl p-6 border border-indigo-400/30">
          <h4 
            className="text-indigo-200 text-lg font-bold mb-4 text-center"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            🛠️ Consciousness Tools & Workflows (Coming Soon)
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Button
              onClick={() => alert('Business Model Generation - Analog simulation workflow for consciousness-driven ventures coming soon...')}
              variant="outline"
              size="sm"
              className="text-indigo-300 border-indigo-400/50 hover:bg-indigo-500/20"
            >
              <Target className="w-3 h-3 mr-1" />
              Business Model Gen
            </Button>
            
            <Button
              onClick={() => alert('Vision Creation Worksheet - Beyond Entrepreneurship 2.0 methodology for consciousness expansion coming soon...')}
              variant="outline"
              size="sm"
              className="text-purple-300 border-purple-400/50 hover:bg-purple-500/20"
            >
              <Star className="w-3 h-3 mr-1" />
              Vision Worksheet
            </Button>
            
            <Button
              onClick={() => alert('Brand-Name Builder - Consciousness-aligned brand creation tools coming soon...')}
              variant="outline"
              size="sm"
              className="text-cyan-300 border-cyan-400/50 hover:bg-cyan-500/20"
            >
              <Heart className="w-3 h-3 mr-1" />
              Brand Builder
            </Button>
            
            <Button
              onClick={() => alert('Eight Compass Point Phases - WisconSing methodology for project completion coming soon...')}
              variant="outline"
              size="sm"
              className="text-yellow-300 border-yellow-400/50 hover:bg-yellow-500/20"
            >
              <Compass className="w-3 h-3 mr-1" />
              8 Compass Points
            </Button>
            
            <Button
              onClick={() => alert('Retreat/Event Producer - Consciousness gathering coordination tools coming soon...')}
              variant="outline"
              size="sm"
              className="text-green-300 border-green-400/50 hover:bg-green-500/20"
            >
              <Users className="w-3 h-3 mr-1" />
              Event Producer
            </Button>
            
            <Button
              onClick={() => alert('Conscious Stock Contribution - Trust-based investment collaboration coming soon...')}
              variant="outline"
              size="sm"
              className="text-orange-300 border-orange-400/50 hover:bg-orange-500/20"
            >
              <TrendingUp className="w-3 h-3 mr-1" />
              Stock Contribution
            </Button>
          </div>
          
          <p 
            className="text-indigo-100 text-xs text-center mt-4 italic"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            Revolutionary consciousness-driven business and collaboration tools
          </p>
        </div>
        </div>
        
        <UniversalHeartToggle />
        <PersistentFooter />
      </div>
    );
  }

  // DEFAULT VIEW - Should not reach here
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <p className="text-white">Loading Project Salons...</p>
    </div>
  );
};

export default MessagingPage;